import { join } from "path";
import findUp from "find-up";
import fs from "mz/fs";
import { exec } from "mz/child_process";
import { URL } from "url";
import fetch from "node-fetch";
import { isDate, inspect } from "util";
import jwt from "jsonwebtoken";
import log from "@pagedip/util-logger";
import {
  LicenseUnknownError,
  PagedipError,
  LicenseMissingError,
  LicenseInvalidError,
  LicenseExpiredError,
} from "@pagedip/lib-license-errors";

const win = process.platform === "win32";
const home = (win ? process.env.USERPROFILE : process.env.HOME) || "";

const licenseServerUrl = "https://license.system.pagedip.com";
const licenseServerHost = (() => {
  const url = new URL(licenseServerUrl);
  return url.host;
})();

/** Main file path for the pdlicense file. Depends on platform. */
export const installPath = home ? join(home, ".pdlicense") : join(win ? "/etc" : "/usr/local/etc", "pdlicense");

const licenseFiles = [join("/etc", "pdlicense"), join("/usr/local/etc", "pdlicense"), join(home, ".pdlicense")];

export interface LicenseData {
  original: string;
  id: string;
  user: string;
  payload: Record<string, any>;
  revoked: boolean;
  expired: boolean;
  ttl: Date;
  issued: Date;
}

export namespace LicenseData {
  export function is(v: any): v is LicenseData {
    return (
      Boolean(v) &&
      typeof v.original === "string" &&
      Boolean(v.original) &&
      typeof v.id === "string" &&
      Boolean(v.id) &&
      typeof v.user === "string" &&
      Boolean(v.user) &&
      typeof v.payload === "object" &&
      v.payload != null &&
      typeof v.revoked === "boolean" &&
      typeof v.expired === "boolean" &&
      isDate(v.ttl) &&
      !isNaN(v.ttl) &&
      isDate(v.issued) &&
      !isNaN(v.issued)
    );
  }
}

/**
 * Attempts to locate the raw license string on the system. First, it directly returns any license passed as an
 * argument. Next, it checks the `pagedip_license` environment variable. Lastly, it checks known file paths for a
 * pdlicense file.
 */
export function findLicenseString(license?: string) {
  if (license) return license;
  if (process.env.pagedip_license) return process.env.pagedip_license;

  let file = findUp.sync(".pdlicense");

  if (!file)
    for (let i = 0; i < licenseFiles.length; i++) {
      try {
        const stat = fs.statSync(licenseFiles[i]);
        if (stat.isFile()) {
          file = licenseFiles[i];
          break;
        }
      } catch (e) {
        continue;
      }
    }

  if (file) {
    return fs.readFileSync(file, "utf-8").trim();
  }
}

/**
 * Checks a license string against the license server and returns the results.
 */
export async function verify(license: string) {
  if (!license) {
    throw new LicenseMissingError();
  }

  const resp = await fetch(`${licenseServerUrl}/license/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "text/plain; charset=utf-8",
    },
    body: license,
  });

  let body;
  try {
    body = await resp.json();
  } catch (e) {
    throw new LicenseInvalidError("Invalid response from license server.");
  }

  if (!resp.ok || !body.ok) {
    if (body.error) {
      throw new PagedipError(body);
    } else {
      throw new LicenseUnknownError();
    }
  }

  const { id, user, payload, revoked, expired, ttl, issued } = body;

  const result = {
    id,
    user,
    payload,
    revoked,
    expired,
    ttl: ttl ? new Date(ttl) : undefined,
    issued: issued ? new Date(issued) : undefined,
    original: license,
  };

  if (!LicenseData.is(result)) {
    throw new LicenseInvalidError("License result had an incorrect format.");
  }

  return result;
}

/** Find and verifies the license in one step. */
export async function findAndVerify(license = findLicenseString()) {
  if (!license) {
    throw new LicenseMissingError();
  }

  return verify(license);
}

/** Install a license string onto the file system and setup NPM to use the license server registry. */
export async function install(license: string) {
  if (!license) {
    throw new LicenseMissingError();
  }

  await fs.writeFile(installPath, license);
  await exec(`npm config set @pagedip:registry ${licenseServerUrl}/npm/`);
  await exec(`npm config set @beneaththeink:registry ${licenseServerUrl}/npm/`);
  await exec(`npm config set //${licenseServerHost}/npm/:_authToken ${license}`);
}

/** Inverse of install(), this will remove the license file and remove license server NPM configuration. */
export async function uninstall() {
  await exec(`npm config delete @pagedip:registry`);
  await exec(`npm config delete @beneaththeink:registry`);
  await exec(`npm config delete //${licenseServerHost}/:_authToken`);
  await exec(`npm config delete //${licenseServerHost}/npm/:_authToken`);

  try {
    await fs.unlink(installPath);
  } catch (e) {
    if (e.code !== "ENOENT") throw e;
  }
}

const publicKeys = [
  `-----BEGIN PUBLIC KEY-----
MIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEApOGsKsYBfJAcXp7uIvz3
WgbyIEhus+bjO1EAoKrudbSBA/zaBd0d9Og51icfqQGjJ4KLv4KoYMj6cJCmaQw1
ChxFbUTSG1FEsIzNSApiKXnxRTecblB9177zgLBNRtwHg2Ko7YKtBkyBlJu2qBnN
ok5Vms676RZzKo+CBL9sfdKkD0rt8TLfsCQRRACxDoRYxeeCU4ZOTAJgsQTsD1bI
eoOBBD8YbksMYA/vCYDfcIBeAYbLPIaLtj4V3aUWu5++MWLkehmAxf/3WdTNyfEB
bJ3DJv15/bh+JewU/jsDBcgPEiMvRoWgCOQ4UGu9XPq5TU3QZRkJBBqU/YrWE5k4
NrqaozhvknoLBdxiIl5dhnLuZ//MBzHsuIJTMhKCiB76llM+XIWn0rXh+mQ1vsri
Y09Ua2yabJYF30VM6Xr2lpD7x5hcoKIbTPFna+Fcv+M4TwwP9LFdXh5ORQz9DEYC
ftQnaUBcZzpiyvawWincP7A8MQU3udz3Wn5gGDr3AZ8CifRChI2jP4Y4WQPi+X9R
Ud+Z1opGudiPVC41U2YypfYtZipkAwISTt26qDfNQ50ZzbbGlDQ8alUf6XyRXhXZ
lmZ3XW+e+GwLHmP1tthEb9FSc3asM09V4WZ3wc5GlZfeEkZYc32vUdHgIgFnBOwy
QFFWXF6/VNUs7soEfoYUc50CAwEAAQ==
-----END PUBLIC KEY-----`,
];

/** Similar to verify(), but does the verification without network requests using known license server public keys. */
export function verifyOffline(license: string) {
  if (!license) {
    throw new LicenseMissingError();
  }

  let data: any;

  for (let i = 0; i < publicKeys.length; i++) {
    try {
      const decoded = jwt.verify(license, publicKeys[i], {
        algorithms: ["RS256"],
      });

      if (typeof decoded === "object" && decoded != null) {
        data = decoded;
        break;
      }
    } catch (e) {
      if (e.message.match(/jwt expired/i)) {
        throw new LicenseExpiredError();
      }

      if (!e.message.match(/invalid signature/i)) {
        throw e;
      }
    }
  }

  if (!data) {
    throw new LicenseInvalidError("License signature is invalid.");
  }

  const { exp, iat, aud, sub, iss, ...payload } = data;

  const result = {
    id: sub,
    user: aud,
    payload,
    revoked: false,
    expired: false,
    ttl: exp ? new Date(exp * 1000) : undefined,
    issued: iat ? new Date(iat * 1000) : undefined,
    original: license,
  };

  if (!LicenseData.is(result)) {
    throw new LicenseInvalidError("License result had an incorrect format.");
  }

  return result;
}

/** Find and verifies the license offline in one step. */
export function findAndVerifyOffline(license = findLicenseString()) {
  if (!license) {
    throw new LicenseMissingError();
  }

  return verifyOffline(license);
}

/** Print license data in a human friendly format. */
export function print(data?: LicenseData, opts: { colors?: boolean } = {}) {
  const { colors = true } = opts;

  if (LicenseData.is(data)) {
    const msg = "Valid Pagedip license key.";

    return `${colors ? log.color.white.bgGreen(msg) : msg}
ID: ${data.id}
Registered To: ${data.user}
Issued: ${data.issued.toLocaleString()}
Expires: ${data.issued.toLocaleString()}

${inspect(data.payload, { colors, depth: Infinity })}`;
  } else {
    const msg = "Invalid Pagedip license key.";
    return colors ? log.color.white.bgRed(msg) : msg;
  }
}
